import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, LogIn, ArrowLeft, Eye } from 'lucide-react';
import { toast } from 'sonner';
import ComplianceFooter from '../components/ComplianceFooter';

const SOURCE_BANNER = {
  founding_member: { title: '🎁 Founding Member Welcome', body: 'Login or register to claim your founding-member benefits.' },
  buyer: { title: 'Welcome, Society / Facility Manager', body: 'Sign in to publish tenders, or register your society to get started.' },
  supplier: { title: 'Welcome, Service Vendor', body: 'Sign in to view tenders, submit bids and grow your business.' },
  buyer_demo_top: { title: 'Continue from the Buyer Demo', body: 'Sign in to your portal, or register as a Society / Facility.' },
  buyer_journey_completed: { title: 'You finished the Buyer Journey', body: 'Sign in to run a real tender end-to-end.' },
  buyer_journey_footer: { title: 'Ready to start as a Buyer', body: 'Sign in or register as a Society / Facility Manager.' },
  supplier_demo_top: { title: 'Continue from the Supplier Demo', body: 'Sign in to bid on tenders, or register as a Vendor.' },
  supplier_journey_completed: { title: 'You finished the Supplier Journey', body: 'Sign in to start winning contracts.' },
  supplier_journey_footer: { title: 'Ready to start as a Supplier', body: 'Sign in or register as a Service Vendor.' },
  access_portal_home: { title: 'Welcome back', body: 'Sign in to your ProcureOne Ultra portal.' },
  hero_access_portal: { title: 'Welcome to ProcureOne Ultra', body: 'Sign in to your portal, or register as a Society, Facility Manager, or Vendor.' },
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') || null;
  const banner = source ? SOURCE_BANNER[source] : null;
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (source) {
      try { localStorage.setItem('pou_lead_source', source); } catch (e) { /* ignore */ }
    }
  }, [source]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(formData.email, formData.password);
      toast.success('Login successful!');
      
      if (user.role === 'buyer') {
        navigate('/buyer-dashboard');
      } else if (user.role === 'supplier') {
        navigate('/supplier-dashboard');
      } else if (user.role === 'admin') {
        navigate('/admin-dashboard');
      } else if (user.role === 'referral_partner' || user.role === 'dsa') {
        navigate('/partner-dashboard');
      } else if (user.role === 'committee_member') {
        navigate('/committee/dashboard');
      } else if (user.role === 'committee_observer') {
        navigate('/observer/dashboard');
      } else {
        navigate('/');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Login failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/buyer-dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            data-testid="login-back-home-link"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Home
          </Link>
        </div>
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img
              src="/plus-ultra-logo.webp"
              alt="Plus Ultra Innovation"
              className="h-14 w-14 rounded-full object-contain bg-white ring-1 ring-gold/50"
              data-testid="login-logo-img"
            />
            <span className="text-3xl font-bold tracking-tight" style={{ color: 'hsl(var(--primary))' }}>ProcureOne Ultra</span>
          </div>
          <h1 className="text-2xl font-semibold">Business Login</h1>
          <p className="text-muted-foreground mt-2">For Buyer · Supplier · FMS · Admin · DSA · Partner · Committee Members</p>
        </div>

        {banner && (
          <div className="bg-gold/10 border-2 border-gold/30 rounded-lg p-4 mb-5 text-center" data-testid="login-source-banner">
            <div className="text-sm font-bold text-navy" data-testid="login-source-banner-title">{banner.title}</div>
            <div className="text-xs text-foreground/80 mt-1" data-testid="login-source-banner-body">{banner.body}</div>
          </div>
        )}
        <div className="bg-card border border-border rounded-lg p-8 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">Email or Username</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  type="text"
                  autoComplete="username"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full pl-10 pr-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                  placeholder="you@example.com  or  chairman_shah"
                  data-testid="login-email-input"
                />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Committee Members: use the username you set at first-time activation.</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="w-full pl-10 pr-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : (
                <>
                  <LogIn className="mr-2 h-4 w-4" /> Sign In
                </>
              )}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-card text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <button
              onClick={handleGoogleLogin}
              type="button"
              className="mt-4 w-full btn-outline"
              data-testid="google-login-btn"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to={source && source.startsWith('buyer') ? '/buyer-register' : source && source.startsWith('supplier') ? '/supplier-register' : '/register'} className="font-medium text-primary hover:underline" data-testid="register-link">
              Register here
            </Link>
          </p>
          <p className="mt-2 text-center text-sm">
            <Link to="/forgot-password" className="text-muted-foreground hover:underline" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </p>
          <p className="mt-2 text-center text-[11px] text-muted-foreground/80" data-testid="login-role-hint">
            One sign-in serves <strong>Buyers</strong>, <strong>Suppliers</strong>, <strong>Admins</strong>, <strong>Partners</strong>, and <strong>Committee Members</strong> — your role is determined by your account.
          </p>
          <div className="mt-6 pt-5 border-t border-border">
            <p className="text-center text-xs text-muted-foreground mb-3">Society Observer? Use the dedicated view-only login.</p>
            <Link
              to="/observer-login"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border-2 border-primary text-primary font-bold hover:bg-primary hover:text-primary-foreground transition-colors"
              data-testid="btn-observer-login"
            >
              <Eye className="w-4 h-4" />
              Society Observer Login
            </Link>
          </div>
        </div>
        <ComplianceFooter className="mt-8" />
      </div>
    </div>
  );
};

export default Login;
